import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { API_URL } from '../../config';
import { mockAuthHeaders } from '../../utils/mockAuth';
// getProfile removed - using graphData from parent now
import ResumePreviewPanel from './panels/ResumePreviewPanel';
import ToolsPanel from './panels/ToolsPanel';

/**
 * ResumeBuilderSection - Two-panel resume builder
 * 
 * Layout:
 * [LEFT: Resume Preview] [RIGHT: Tools (Profile/Config/Criteria tabs)]
 * 
 * State is managed here and passed down to child panels.
 */
const ResumeBuilderSection = ({ application, profile, jobDescription, graphData, onToggleSelection, onUpdate }) => {
  // Data state - all selection comes from graphData, no local state needed
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Get selected items from graphData
  const selectedItems = graphData?.getSelectedNodes() || [];

  // Resume config state
  const [resumeConfig, setResumeConfig] = useState({
    template: 'modern',
    targetPages: 2,
    selectedItemIds: []
  });

  // Profile data comes from graphData prop - no separate loading needed

  // Load existing resume config
  useEffect(() => {
    loadResumeConfig();
  }, [application?.id]);

  // loadProfileData removed - using graphData.profileNodes from parent

  const loadResumeConfig = async () => {
    if (!application?.id) return;

    try {
      const response = await fetch(
        `${API_URL}/api/applications/${application.id}/resume-config`,
        {
          headers: { ...mockAuthHeaders() },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.resume_config) {
          setResumeConfig(data.resume_config);
          // selectedItems now comes from graphData.getSelectedNodes(), not loaded here
        }
      }
    } catch (err) {
      console.error('Error loading resume config:', err);
      // Continue with empty config
    }
  };

  // Selection management removed - using onToggleSelection from parent
  // Old handleAddItem, handleRemoveItem, handleReorderItems removed
  // Use onToggleSelection prop instead

  // Save resume config to backend
  const saveResumeConfig = async (config) => {
    if (!application?.id) return;

    try {
      await fetch(
        `${API_URL}/api/applications/${application.id}/resume`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...mockAuthHeaders()
          },
          credentials: 'include',
          body: JSON.stringify({ resume_config: config })
        }
      );

    } catch (err) {
      console.error('Error saving resume config:', err);
    }
  };

  // Handle Save button click
  const handleSave = async () => {
    if (!application?.id) return;
    
    setSaving(true);
    try {
      // Build config with selected items
      const configToSave = {
        ...resumeConfig,
        selected_items: selectedItems.map(item => item.item_id || item.id),
        application_id: application.id,
        updated_at: new Date().toISOString()
      };

      const response = await fetch(
        `${API_URL}/api/applications/${application.id}/resume-config`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...mockAuthHeaders()
          },
          credentials: 'include',
          body: JSON.stringify(configToSave)
        }
      );

      if (response.ok) {
        // Optionally show success message
        if (onUpdate) onUpdate();
      } else {
        throw new Error('Failed to save resume config');
      }
    } catch (err) {
      console.error('[ResumeBuilder] ❌ Error saving:', err);
      setError('Failed to save resume configuration');
    } finally {
      setSaving(false);
    }
  };

  // Handle Export PDF button click
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      
      // Use browser's print dialog to save as PDF
      // This is the most reliable cross-browser method
      // The resume is already styled with print-friendly CSS
      
      // First, save current state
      await handleSave();
      
      // Trigger print dialog
      // User can then "Save as PDF" from the print dialog
      window.print();
      
    } catch (err) {
      console.error('[ResumeBuilder] Error exporting PDF:', err);
      setError('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Paper elevation={1} sx={{ p: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">
              Resume Builder
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedItems.length} items selected
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !application?.id}
              size="small"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={16} /> : <PdfIcon />}
              onClick={handleExportPDF}
              disabled={exporting || selectedItems.length === 0}
              size="small"
            >
              {exporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Two-Panel Layout */}
      <Grid container sx={{ minHeight: 'calc(100vh - 400px)' }}>
        {/* LEFT PANEL: Resume Preview */}
        <Grid
          item
          xs={12}
          md={8}
          sx={{
            borderRight: { md: 1 },
            borderBottom: { xs: 1, md: 0 },
            borderColor: 'divider',
            maxHeight: { md: 'calc(100vh - 400px)' },
            overflowY: 'auto',
            bgcolor: 'grey.50'
          }}
        >
          <ResumePreviewPanel
            profile={profile}
            selectedItems={selectedItems}
            resumeConfig={resumeConfig}
            application={application}
            graphData={graphData}
          />
        </Grid>

        {/* RIGHT PANEL: Tools (Profile/Config/Criteria tabs) */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            maxHeight: { md: 'calc(100vh - 400px)' },
            overflowY: 'auto'
          }}
        >
          <ToolsPanel
            profile={profile}
            selectedItems={selectedItems}
            resumeConfig={resumeConfig}
            onUpdateConfig={setResumeConfig}
            jobDescription={jobDescription}
            graphData={graphData}
            onToggleSelection={onToggleSelection}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ResumeBuilderSection;
